import { serializeParams } from "@/utils/apiUtils";
import { AxiosPromise } from "axios";
import { ApiRequestAdapter } from "../../../common/api/ApiRequestAdapter";
import { apiService } from "../../../common/api/apiService";
import { HttpMethod } from "../../../common/api/http";
import {
  EntityObject,
  OptimisticLockVersionList,
  PageRequest,
  PageResult,
  TwoLevelEntityIdObject,
  TwoLevelEntityObject,
} from "../../../common/types";
import { AGENT_FILTER_INCLUSION_DEFAULTS, COMMON_AGENT_FILTER_QUERY_PARAMS } from "../../agent/api";
import { CommissionsOutput } from "../batch/types";
import {
  SpecialCommissionBase,
  SpecialCommissionsFilterPageRequest,
  SpecialCommissionsFilterPageResult,
} from "../special/types";
import { CommissionsOutputReport } from "./enums";
import {
  CalculatedCommissionsFilterExportRequest,
  CalculatedCommissionsFilterPageRequest,
  CalculatedCommissionsFilterPageResult,
  CommissionsOutputFilterExportRequest,
  CommissionsOutputFilterPageRequest,
  CommissionsOutputFilterPageResult,
  CreateUpdateCommissionsOutput,
} from "./types";

const FILTER_COMMISSIONS_OUTPUTS = ApiRequestAdapter.create("commissions-outputs", HttpMethod.GET);
const DOWNLOAD_COMMISSIONS_OUTPUTS_EXPORT = ApiRequestAdapter.create("commissions-outputs/export", HttpMethod.GET);
const TOGGLE_PAID_COMMISSIONS_OUTPUT = ApiRequestAdapter.create("/commissions-outputs/toggle-paid", HttpMethod.POST);

const FILTER_AGENT_COMMISSIONS_OUTPUTS = ApiRequestAdapter.create("/agents/{0}/commissions-outputs", HttpMethod.GET);
const CREATE_AGENT_COMMISSIONS_OUTPUT = ApiRequestAdapter.create("agents/{0}/commissions-outputs", HttpMethod.POST);
const UPDATE_AGENT_COMMISSIONS_OUTPUT = ApiRequestAdapter.create("agents/{0}/commissions-outputs/{1}", HttpMethod.PUT);

const DELETE_AGENT_COMMISSIONS_OUTPUT = ApiRequestAdapter.create(
  "agents/{0}/commissions-outputs/{1}",
  HttpMethod.DELETE,
);
const DOWNLOAD_AGENT_COMMISSIONS_OUTPUT = ApiRequestAdapter.create(
  "/agents/{0}/commissions-outputs/{1}",
  HttpMethod.GET,
);

const FILTER_CALCULATED_COMMISSIONS_FOR_AGENT_OUTPUTS = ApiRequestAdapter.create(
  "/agents/{0}/commissions-outputs/{1}/calculated-commissions",
  HttpMethod.GET,
);
const DOWNLOAD_CALCULATED_COMMISSIONS_EXPORT_FOR_AGENT_OUTPUTS = ApiRequestAdapter.create(
  "/agents/{0}/commissions-outputs/{1}/calculated-commissions/export",
  HttpMethod.GET,
);

const FILTER_SPECIAL_COMMISSIONS_FOR_AGENT_OUTPUTS = ApiRequestAdapter.create(
  "/agents/{0}/commissions-outputs/{1}/special-commissions",
  HttpMethod.GET,
);

export const commissionOutputApi = {
  filterCommissionsOutputs: (
    request: CommissionsOutputFilterPageRequest,
  ): AxiosPromise<CommissionsOutputFilterPageResult> => {
    return apiService.get(FILTER_COMMISSIONS_OUTPUTS.url, {
      params: request,
      paramsSerializer: { serialize: serializeParams },
    });
  },
  downloadCommissionsOutputsExport: (request: CommissionsOutputFilterExportRequest): AxiosPromise<Blob> => {
    return apiService.get(DOWNLOAD_COMMISSIONS_OUTPUTS_EXPORT.url, {
      params: request,
      paramsSerializer: { serialize: serializeParams },
      responseType: "blob",
    });
  },
  filterAgentCommissionsOutputs: (request: EntityObject<PageRequest>): AxiosPromise<PageResult<CommissionsOutput>> => {
    return apiService.get(FILTER_AGENT_COMMISSIONS_OUTPUTS.getParametrizedUrl(request.id), { params: request.object });
  },
  createAgentCommissionsOutput: (
    request: EntityObject<CreateUpdateCommissionsOutput>,
  ): AxiosPromise<CommissionsOutput> => {
    return apiService.post(CREATE_AGENT_COMMISSIONS_OUTPUT.getParametrizedUrl(request.id), request.object);
  },
  updateAgentCommissionsOutput: (
    request: TwoLevelEntityObject<CreateUpdateCommissionsOutput>,
  ): AxiosPromise<CommissionsOutput> => {
    return apiService.put(UPDATE_AGENT_COMMISSIONS_OUTPUT.getParametrizedUrl(request.id1, request.id2), request.object);
  },
  togglePaidCommissionsOutput: (request: OptimisticLockVersionList): AxiosPromise<CommissionsOutput[]> => {
    return apiService.post(TOGGLE_PAID_COMMISSIONS_OUTPUT.url, request);
  },
  deleteAgentCommissionsOutput: (request: TwoLevelEntityIdObject): AxiosPromise => {
    return apiService.delete(DELETE_AGENT_COMMISSIONS_OUTPUT.getParametrizedUrl(request.id1, request.id2));
  },
  downloadAgentCommissionsOutput: (request: TwoLevelEntityIdObject): AxiosPromise<Blob> => {
    return apiService.get(DOWNLOAD_AGENT_COMMISSIONS_OUTPUT.getParametrizedUrl(request.id1, request.id2), {
      responseType: "blob",
    });
  },
  filterCalculatedCommissionsForAgentOutputs: (
    request: TwoLevelEntityObject<CalculatedCommissionsFilterPageRequest>,
  ): AxiosPromise<CalculatedCommissionsFilterPageResult> => {
    return apiService.get(
      FILTER_CALCULATED_COMMISSIONS_FOR_AGENT_OUTPUTS.getParametrizedUrl(request.id1, request.id2),
      {
        params: request.object,
        paramsSerializer: { serialize: serializeParams },
      },
    );
  },
  downloadCalculatedCommissionsExportForAgentOutputs: (
    request: TwoLevelEntityObject<CalculatedCommissionsFilterExportRequest>,
  ) => {
    return apiService.get(
      DOWNLOAD_CALCULATED_COMMISSIONS_EXPORT_FOR_AGENT_OUTPUTS.getParametrizedUrl(request.id1, request.id2),
      {
        params: request.object,
        paramsSerializer: { serialize: serializeParams },
        responseType: "blob",
      },
    );
  },
  filterSpecialCommissionsForAgentOutputs: (
    request: TwoLevelEntityObject<SpecialCommissionsFilterPageRequest>,
  ): AxiosPromise<SpecialCommissionsFilterPageResult<SpecialCommissionBase>> => {
    return apiService.get(FILTER_SPECIAL_COMMISSIONS_FOR_AGENT_OUTPUTS.getParametrizedUrl(request.id1, request.id2), {
      params: request.object,
    });
  },
};

export const DEFAULT_FILTER_COMMISSIONS_OUTPUTS_QUERY_PARAMS: Required<CommissionsOutputFilterPageRequest> = {
  ...COMMON_AGENT_FILTER_QUERY_PARAMS,
  ...AGENT_FILTER_INCLUSION_DEFAULTS.NO_REPRESENTATIVES,
  report: CommissionsOutputReport.ALL,
  batchKeyword: "",
  categories: [],
  includeUnprocessed: false,
};
